/**
 * Batched User Avatar Hook
 * 
 * Fetches user data (name, full_name, user_image) for a list of user IDs/emails
 * in a single API call. Results are cached by React Query.
 */

import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { getQueryOptions } from "@/lib/config/queryConfig";

export type UserAvatarInfo = {
  id: string; // user name / email
  fullName?: string;
  imageUrl?: string;
};

export type UserAvatarMap = Record<string, UserAvatarInfo>;

/**
 * Hook to fetch user avatars in batch
 * 
 * @param userIds - Array of user IDs/emails to fetch avatars for
 * @returns Map of userId -> UserAvatarInfo, plus loading state
 */
export function useUserAvatars(userIds: string[]): {
  avatarMap: UserAvatarMap;
  isLoading: boolean;
} {
  // Deduplicate and filter out empty strings
  const uniqueUserIds = Array.from(new Set(userIds.filter((id) => id?.trim())));

  const { data, isLoading } = useQuery({
    queryKey: ["user-avatars", uniqueUserIds.sort().join(",")],
    queryFn: async (): Promise<UserAvatarMap> => {
      if (uniqueUserIds.length === 0) {
        return {};
      }

      // Fetch users in batch using Frappe's list API
      // Only request allowed fields to avoid "Field not permitted in query" errors
      // Note: Frappe filter format for "in" operator
      const users = await erp.users.list({
        filters: {
          name: ["in", uniqueUserIds] as any, // Frappe accepts ["in", array] format
        },
        fields: ["name", "full_name", "user_image"],
      });

      // Build map: userId -> UserAvatarInfo
      const avatarMap: UserAvatarMap = {};

      for (const user of users) {
        const userId = user.name;
        // user_image is typically a file path, construct the full URL
        // Frappe file URLs are typically: /files/{file_path}
        let imageUrl: string | undefined;
        if (user.user_image) {
          // If it's already a full URL, use it; otherwise construct relative path
          if (user.user_image.startsWith("http://") || user.user_image.startsWith("https://")) {
            imageUrl = user.user_image;
          } else {
            // Frappe file paths are typically relative to /files/
            // If it doesn't start with /files/, prepend it
            const cleanPath = user.user_image.startsWith("/files/")
              ? user.user_image
              : user.user_image.startsWith("/")
              ? user.user_image
              : `/files/${user.user_image}`;
            imageUrl = cleanPath;
          }
        }

        avatarMap[userId] = {
          id: userId,
          fullName: user.full_name || undefined,
          imageUrl,
        };
      }

      // Ensure all requested userIds have an entry (even if not found)
      for (const userId of uniqueUserIds) {
        if (!avatarMap[userId]) {
          avatarMap[userId] = {
            id: userId,
          };
        }
      }

      return avatarMap;
    },
    enabled: uniqueUserIds.length > 0,
    ...getQueryOptions("list"),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    avatarMap: data || {},
    isLoading,
  };
}

